import React, { FC, useState } from 'react';
import { Box } from '@mui/material';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Link from '@mui/material/Link';
import Tooltip from '@mui/material/Tooltip';
import { useRequest } from 'ahooks';
import type { DepositRow, PaginatedHistory, PayoutRow } from '@/api/feeDashboard';
import { formatRaw } from '@/utils/feeBalances';
import {
	Card,
	chainByWormholeId,
	ChainLogo,
	explorerTxUrl,
	formatDate,
	Mono,
	Muted,
	Pager,
	Pill,
	Row,
	shortHash,
} from './styles';

type HistoryRow = PayoutRow | DepositRow;

type Props = {
	pageSize: number;
	emptyLabel: string;
	fetchPage: (offset: number) => Promise<PaginatedHistory<HistoryRow>>;
};

const KIND_LABELS: Record<string, string> = {
	redeem: 'Group payout',
	sweep: 'Paid out',
	withdraw: 'Withdrawn',
};

// Shared table for the two history tabs. Both endpoints return the same shape
// plus `recordedSince`, which is surfaced prominently: the index does not reach
// back to the fee manager's deployment, so an empty list must not read as
// "never paid".
const FeeHistory: FC<Props> = ({ pageSize, emptyLabel, fetchPage }) => {
	const [offset, setOffset] = useState(0);
	const { data, loading } = useRequest(async () => fetchPage(offset), { refreshDeps: [offset] });

	const rows = data?.data ?? [];
	const total = data?.total ?? 0;
	const pageCount = Math.max(1, Math.ceil(total / pageSize));
	const page = Math.floor(offset / pageSize) + 1;

	return (
		<Box sx={{ width: 1 }}>
			<Card>
				{loading && rows.length === 0 && (
					<Row>
						<Muted variant="appSmallRegular">Loading…</Muted>
					</Row>
				)}
				{!loading && rows.length === 0 && (
					<Row>
						<Muted variant="appSmallRegular">{emptyLabel}</Muted>
					</Row>
				)}
				{rows.map((row, i) => (
					<HistoryEntry key={`${row.txHash}-${i}`} row={row} divider={i < rows.length - 1} />
				))}
			</Card>

			{data?.recordedSince && (
				<Muted variant="appTinyRegular" sx={{ display: 'block', mt: 1.5, textAlign: 'center' }}>
					Showing activity recorded since {formatDate(data.recordedSince)}. Anything before that date is
					not indexed.
				</Muted>
			)}

			{total > pageSize && (
				<Pager>
					<Button size="small" disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - pageSize))}>
						Previous
					</Button>
					<Muted variant="appTinyRegular">
						Page {page} of {pageCount} · {total} entries
					</Muted>
					<Button size="small" disabled={offset + pageSize >= total} onClick={() => setOffset(offset + pageSize)}>
						Next
					</Button>
				</Pager>
			)}
		</Box>
	);
};

const HistoryEntry: FC<{ row: HistoryRow; divider: boolean }> = ({ row, divider }) => {
	const chain = chainByWormholeId(row.chainId);
	const url = explorerTxUrl(row.chainId, row.txHash);
	const kind = 'kind' in row ? row.kind : 'deposit';
	const { decimals, symbol } = row;

	return (
		<Row divider={divider}>
			<Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0 }}>
				{chain?.logoURI && <ChainLogo src={chain.logoURI} alt={chain.chainName} />}
				<Box sx={{ minWidth: 0 }}>
					<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
						<Typography variant="appSmallRegular" color="common.textPrimary">
							{KIND_LABELS[kind] ?? 'Fee'}
						</Typography>
						{'actor' in row && row.actor && (
							<Tooltip title={`Triggered by ${row.actor}. Anyone can trigger a group payout — it can only ever pay the committed members.`}>
								<Pill tone="neutral">on-chain</Pill>
							</Tooltip>
						)}
					</Box>
					<Muted variant="appTinyRegular" sx={{ display: 'block' }}>
						{formatDate(row.blockTime)} ·{' '}
						{url ? (
							<Link href={url} target="_blank" rel="noreferrer" color="inherit" underline="hover">
								{shortHash(row.txHash)}
							</Link>
						) : (
							shortHash(row.txHash)
						)}
					</Muted>
				</Box>
			</Box>

			<Box sx={{ textAlign: 'right', flexShrink: 0 }}>
				<Mono variant="appSmallRegular" color="common.textPrimary">
					{formatRaw(row.yourAmount, decimals)}
					{symbol ? ` ${symbol}` : ''}
				</Mono>
				{row.totalAmount && row.totalAmount !== row.yourAmount && (
					<Muted variant="appTinyRegular" sx={{ display: 'block' }}>
						of {formatRaw(row.totalAmount, decimals)}
						{row.yourBps ? ` · ${row.yourBps} bps` : ''}
					</Muted>
				)}
			</Box>
		</Row>
	);
};

export default FeeHistory;
